// Status-tone mapping — ATC-109 / `ATC-ADR-018` anchor 5.
//
// Nothing in `FD-ADR-038` maps ATC's own status vocabularies. This mapping is
// ATC's decision and is recorded in `ATC-ADR-018` so it is not re-derived every
// time someone adds a surface. Two of the calls are judgement calls and the ADR
// says which and why; do not "fix" them here without amending the ADR.
//
// THE GOVERNING RULE: never let a real status value fall through to neutral.
// `FD-197` was a faulted device that rendered as a neutral grey chip and looked
// benign in the device grid for as long as the terminal vocabulary went
// unhandled. An UNRECOGNIZED value renders WARNING — we do not know what it is,
// so an operator should look.

import type { ActivityOutcome, EnvironmentStatus } from '../api/types'
import type { OperatorRoleName } from '../auth/roles'
import type { ExpiryState } from '../api/expiry'
import { STATUS } from './tokens'

/**
 * The five tones. This union is ATC-OWNED and closed — no server can widen it,
 * which is why `assertNever` may legitimately throw on it (below).
 */
export type StatusTone = keyof typeof STATUS

export interface ToneVisual {
  /**
   * The status hex. A GRAPHICAL value only — a dot or a bar. Never text: on
   * white, success is 3.30:1 and warning 3.19:1, and both fail 4.5:1.
   */
  hex: string
  /** The tint fill behind the chip. */
  tint: string
}

/**
 * Compile-time exhaustiveness guard for an ATC-OWNED closed union.
 *
 * The FUNCTION form is mandatory here. The idiomatic `const _x: never = value`
 * does not compile in this project: `tsconfig.json` sets `noUnusedLocals: true`,
 * so the throwaway binding is a BUILD FAILURE rather than a lint warning.
 *
 * Throwing is correct for a union ATC itself defines — reaching this is a
 * programming error, not bad server data. For a union that comes off the WIRE
 * use `unrecognizedWireValue` instead, which does not throw.
 */
export function assertNever(x: never): never {
  throw new Error(`Unhandled variant: ${String(x)}`)
}

/**
 * The runtime half of the guard, for unions that arrive from the server.
 *
 * COMPILE TIME: the parameter is `never`, so once every member of the union has
 * a `case`, TypeScript narrows the `default` branch to `never` and this call
 * type-checks. Add a member without a `case` and the build fails — which is the
 * whole point, per §1.3's "adding a value is a compile error rather than a
 * silent grey chip".
 *
 * RUNTIME: it returns `warning` rather than throwing, because COMPILE-TIME
 * EXHAUSTIVENESS IS NOT RUNTIME SAFETY. Narrowing `status: string` to a union
 * is an assertion about the server, not a constraint on it; the server can
 * still send something outside the union, and a throw would blank a page over a
 * label. Warning, never neutral.
 */
function unrecognizedWireValue(value: never): StatusTone {
  // `value` is `never` to the compiler but a real string at runtime.
  console.warn(
    `[statusTone] unrecognized value ${JSON.stringify(value as unknown)} — rendering as 'warning'`,
  )
  return 'warning'
}

/**
 * Resolve a tone to its dot hex and tint fill.
 *
 * A `Record` lookup would also be exhaustive, but the switch is what carries
 * `assertNever`, and this is the one union where throwing is the right
 * behaviour: `StatusTone` is ATC's own, so an unhandled tone is a bug in this
 * repo rather than a surprise from a server.
 */
export function toneVisual(tone: StatusTone): ToneVisual {
  switch (tone) {
    case 'success':
      return STATUS.success
    case 'error':
      return STATUS.error
    case 'warning':
      return STATUS.warning
    case 'underway':
      return STATUS.underway
    case 'neutral':
      return STATUS.neutral
    default:
      return assertNever(tone)
  }
}

/**
 * Customer-environment lifecycle status → tone.
 *
 * `suspended` → warning: reversible, and it needs a human. This also preserves
 * today's amber.
 *
 * `revoked` → error is a DECIDED AMBIGUITY (`ATC-ADR-018`). It is arguable:
 * revocation is a deliberate terminal admin action rather than a failure, which
 * argues for neutral on the same reading that keeps `aborted`/`salvaged` out of
 * red. Decided error because §1.3 forbids neutral for a real enum value, and
 * because operationally everything using that environment now returns 403 —
 * the same reading that puts "readiness blocked", also a deliberate gate, in
 * the contract's own `error.examples`.
 */
export function environmentStatusTone(status: EnvironmentStatus): StatusTone {
  switch (status) {
    case 'active':
      return 'success'
    case 'suspended':
      return 'warning'
    case 'revoked':
      return 'error'
    default:
      return unrecognizedWireValue(status)
  }
}

/**
 * Activity-feed outcome → tone.
 *
 * The vocabulary is `shared/LoggingHelper.ps1`'s
 * `[ValidateSet('success', 'denied', 'not_found', 'error')]` — note the
 * UNDERSCORE in `not_found`, which breaks any helper that assumes camelCase.
 *
 * THIS IS NOT THE MIGRATION SETTLE OUTCOME. `docs/openapi/atc-migration-licensing.yaml`
 * carries a different, unrelated `success | failure | abort` for settling a
 * migration license. The console renders the LOGGING vocabulary; conflating the
 * two produces a chip for a value this feed never emits.
 *
 * `denied` → warning: an authorization refusal is "a human must act", not a
 * crash.
 *
 * `not_found` → warning is a DECIDED AMBIGUITY (`ATC-ADR-018`) and a VISIBLE
 * BEHAVIOR CHANGE from today's grey. The evidence is inside ATC:
 * `licensing-service/Get-ActivityFeed/run.ps1` deliberately surfaces
 * `not_found` alongside `denied` and `error` in its KQL filter, so the backend
 * already classes it as noteworthy. Combined with the never-fall-through-to-
 * neutral rule, warning.
 */
export function activityOutcomeTone(outcome: ActivityOutcome): StatusTone {
  switch (outcome) {
    case 'success':
      return 'success'
    case 'denied':
      return 'warning'
    case 'not_found':
      return 'warning'
    case 'error':
      return 'error'
    default:
      return unrecognizedWireValue(outcome)
  }
}

/**
 * Licence-expiry state → tone (ATC-129 / `ATC-121`).
 *
 * EXPIRY IS A SECOND, INDEPENDENT AXIS FROM STATUS. An environment can be
 * `active` AND expired: `Get-Entitlements` refuses it with `token_expired`
 * while every list endpoint still reports a green `active`. So this tone is
 * rendered BESIDE the status chip, never folded into it — a green chip on a
 * dead credential is precisely the benign-looking terminal state
 * `ATC-ADR-018` §1.3 exists to prevent.
 *
 * `expired` → error: the credential is refused right now.
 * `expiring` → warning: a human must act, and it is still working.
 * `active` and `none` carry NO TONE (hence `null`) — a perpetual licence is the
 * ordinary case and does not need a chip claiming it is fine. Painting it
 * success would spend the success hue on the absence of a condition.
 *
 * `ExpiryState` is imported as a type rather than redeclared, so the two
 * cannot drift; the exhaustiveness guard below is what makes a new member a
 * build failure here.
 */
export function expiryTone(state: ExpiryState): StatusTone | null {
  switch (state) {
    case 'expired':
      return 'error'
    case 'expiring':
      return 'warning'
    case 'active':
      return null
    case 'none':
      return null
    default:
      // Throwing is right here, unlike the wire-value unions above: an
      // `ExpiryState` is COMPUTED BY THIS CODEBASE, not received from a server,
      // so an unhandled member is a bug in the console rather than a server
      // sending something new.
      return assertNever(state)
  }
}

/**
 * The operator's role badge — NOT a status chip, and deliberately not mapped as
 * one. It is an IDENTITY LABEL, not a state.
 *
 * `operator` renders neutral. `none` carries NO TONE AT ALL (hence `null`),
 * because that condition is already stated in words by the full-width notice
 * the layout renders instead of the page. Painting it red would spend the error
 * hue on a non-error and duplicate a message the surface already makes.
 */
export function operatorRoleTone(role: OperatorRoleName): StatusTone | null {
  return role === 'operator' ? 'neutral' : null
}
